import React from 'react'
import styled from 'styled-components'
import { ProjectTypeId, normalizeProjectTypeId } from '@src/types/ProjectTypeId'
import FormPanel from '@src/components/FormPanel/FormPanel'
import FormPAETable, { PAETableRowType } from '@src/components/Form/PAETable'
import DRSReworkAssetsFields, { DRSReworkAssetDisplay, DRSAssetDepotType } from './ReworkAssetsFields'
import useModal from '@src/components/FormModal/useModal'
import { useSnackbar } from '@src/components/GlobalSnackbar'
import { useParams } from 'react-router'
import { DRSPageType, DRSSectionName, mapDRSPageTypeToPAE } from '@src/modules/DRS/DRSPage'
import { useConfirm } from '@src/components/GlobalConfirmDialog'
import MarginTop from '@src/components/Layout/MarginTop'
import CompCreateButton from '@src/components/Buttons/CreateButton'
import { useDRS, transformSectionNameToDataSectionName } from '@src/modules/DRS/useDRS'
import Modal from '@src/components/FormModal/Modal'
import { useAppStore } from '@src/AppStore'
import _get from 'lodash/get'
import { doubleUp } from '@src/modules/DRS/utils/doubleUp'
import { PAE } from '@src/types/drs/PAE'
import { DRSReworkDetailAndSummary } from '@src/types/drs/rework/Rework'
import { PAETableMoreActionType } from '@src/components/Form/comps/ActionMenu'
import { genericUpdateDRSSection, UpdateDRSSectionViaPathOptions } from '@src/modules/DRS/utils/genericUpdateDRSSection'
import { mapMoreActions } from '@src/modules/DRS/utils/mapMoreActions'
import { transformAssetData } from '../utils/transformFromFormData'

export interface DRSReworkAssetRow extends DRSReworkAssetDisplay {
  _key_: string,
  _type_: PAETableRowType,
  _no_?: number,
  _editable_: boolean, 
}

const ASSET_NULL_VALUES = {
  comments: null,
  hours: null,
  
  mobileAsset: null,
}
const ASSET_ZERO_VALUES = {
  comments: '',
  hours: '0',
 
  mobileAsset: null,
}

interface Props {
  projectTypeId: ProjectTypeId.Asphalt | ProjectTypeId.SpraySeal,
}

const CompDRSReworkAssets = (props: Props) => {
  const {
    projectTypeId,
  } = props
  const normalizedType = normalizeProjectTypeId(projectTypeId)
  const { id } = useParams<{id: string}>()
  const { drs, updateViaPath, updateSectionViaPath, drsPageType, fetchDRS, refresh } = useDRS()
  const isPlan = drsPageType === DRSPageType.Plan
  const isEdit = drsPageType === DRSPageType.Edit
  const { alertError, alertDone } = useSnackbar()
  const { confirm } = useConfirm()
  const [ open, modalData, openAndSetData, closeModal ] = useModal<DRSReworkAssetRow>({})
  const { depotId, stateId } = useAppStore()
  if (drs === null) {
    return null
  }
  const onSave = async (form: DRSReworkAssetRow) => {
    if (modalData === null) {
      addNewAsset(form)
    } else {
      editAsset(form)
    }
  }
  function addNewAsset(form: DRSReworkAssetRow) {
    if (drs === null) {
      return
    }
    const finalForm = transformAssetData(form)
    const retainedDetails = drs.rework[normalizedType].detail
    let retainedSummary = drs.rework[normalizedType].summary
    // always make sure summary has something
    if (retainedSummary === null) {
      retainedSummary = {} as any
    }
    const updatingColumn: PAE.Plan | PAE.Edit = isPlan ? PAE.Plan : PAE.Edit
    const data = {
      summary: retainedSummary,
      detail: [ 
        ...retainedDetails, 
        {
          [updatingColumn]: {
            ...finalForm
          }
        }
      ],
      performed: true,
    }
    update({
      sectionName: DRSSectionName.Rework,
      path: normalizedType,
      data,
    })
  }
  function editAsset(form: DRSReworkAssetRow) {
    const finalForm = transformAssetData(form)
    updateItemByValues(form, finalForm)
  }
  function handleRevert(form: DRSReworkAssetRow) {
    updateItemByValues(form, ASSET_NULL_VALUES)
  }
  function handleMarkZero(form: DRSReworkAssetRow) {
    updateItemByValues(form, ASSET_ZERO_VALUES)
  }
  const handleDelete = (form: DRSReworkAssetRow) => {
    const retainedDetails = drs.rework[normalizedType].detail
    let retainedSummary = drs.rework[normalizedType].summary
    // always make sure summary has something
    if (retainedSummary === null) {
      retainedSummary = {} as any
    }
    if (form._no_ === undefined || form._type_ === undefined) {
      return
    }
    const updatingIndex = form._no_
    const data = {
      summary: retainedSummary,
      detail: [ 
        ...retainedDetails.slice(0, updatingIndex), 
        ...retainedDetails.slice(updatingIndex + 1),
      ],
      performed: true,
    }
    update({
      sectionName: DRSSectionName.Rework,
      path: normalizedType,
      data,
    })
  }
  const updateItemByValues = (form: DRSReworkAssetRow, values: any) => {
    const retainedDetails = drs.rework[normalizedType].detail
    let retainedSummary = drs.rework[normalizedType].summary
    // always make sure summary has something
    if (retainedSummary === null) {
      retainedSummary = {} as any
    }
    const updatingColumn: PAE.Plan | PAE.Edit = isPlan ? PAE.Plan : PAE.Edit
    if (form._no_ === undefined || form._type_ === undefined) {
      return
    }
    const updatingIndex = form._no_
    const retainedAsset = retainedDetails[updatingIndex]
    const data = {
      summary: retainedSummary,
      detail: [ 
        ...retainedDetails.slice(0, updatingIndex), 
        {
          ...retainedAsset,
          [updatingColumn]: {
            ...values
          }
        },
        ...retainedDetails.slice(updatingIndex + 1),
      ],
      performed: true,
    }
    update({
      sectionName: DRSSectionName.Rework,
      path: normalizedType,
      data,
    })
  }
  async function update(options: UpdateDRSSectionViaPathOptions) {
    genericUpdateDRSSection({
      ...options,
      refresh,
      closeModal,
      alertDone,
      alertError,
      updateSectionViaPath,
    })
  }
  function getTableData() {
    if (drs === null) {
      return []
    }
    const assets = (drs.rework[normalizedType] as DRSReworkDetailAndSummary).detail
    if (!assets || !assets.length) {
      return []
    }
    
    const doubled = doubleUp(assets, drsPageType)
    const doubledWithActions = doubled.map((data: any) => mapMoreActions(data, drsPageType))
    return doubledWithActions.map((asset: any) => {
      let assetDepotType = null
      if (asset.mobileAsset && asset.mobileAsset.depot && asset.mobileAsset.depot.id) {
        if (asset.mobileAsset.depot.id === depotId) {
          assetDepotType = {
            id: DRSAssetDepotType.MyDepot,
            description: 'My Depot',
          } 
        } else {
          assetDepotType = {
            id: DRSAssetDepotType.MyState,
            description: 'My State',
          }
        }
      }
      return {
        ...asset,
        assetDepotType,
        assetDescription: asset.mobileAsset ? asset.mobileAsset.description : '',
      }
    }) as any[]
  }
  return <Wrapper>
    <FormPanel title={`Rework Assets`}>
      <FormPAETable<DRSReworkAssetRow>
        fields={DRSReworkAssetsFields}
        data={getTableData()}
        editable={true}
        onEditRow={(row: DRSReworkAssetRow) => {
          openAndSetData({
            ...row,
            hours: { id: row.hours } as any,
          })
        }}
        showMoreActions={isPlan || isEdit}
        onRevert={handleRevert}
        onMarkZero={handleMarkZero}
        onDelete={handleDelete}
        isSelectable={true}
      />
      { (isPlan || isEdit) &&
        <MarginTop>
          <CompCreateButton onClick={() => {
            openAndSetData(null)
          }}>
            Add Rework Asset
          </CompCreateButton>
        </MarginTop>
      }
    </FormPanel>

    { open && 
      <Modal<DRSReworkAssetRow>
        maxWidth={'xs'}
        open={open}
        title='Rework'
        data={{
          rowData: modalData,
          fields: DRSReworkAssetsFields,
          columns: [
            {
              title: 'Asset Information',
              fieldKeys: [
                'assetDepotType', 
                'mobileAsset', 
                'assetDescription', 
                'hours',
              ],
            },
          ],
          extra: {
            depotId,
            stateId,
          }
        }}
        onSuccess={onSave}
        onClose={closeModal}
        onCancel={closeModal}
      />
    }
  </Wrapper>
}

const Wrapper = styled.div`

`

export default CompDRSReworkAssets